import { z } from 'zod';
import { CARD_CONTENT_VERSION, CARD_MAX_LEVEL, cardDefinition } from './cards';
import type { EquippedInstanceInput } from './loadout';
import { CARD_SLOTS, type CardSlot, cardSlotSchema } from './loadout';

/**
 * **As cartas que uma corrida CONGELOU** (gate V28H.H).
 *
 * ## Porque isto é uma projecção e não os três espaços
 *
 * Os três espaços são estado da CONTA e mudam quando o jogador quer. Isto é o
 * que uma sessão de caça guardou no instante em que nasceu, e não muda mais:
 * equipar, remover ou fundir uma carta a meio de uma caçada reconfigura a
 * conta e não a corrida. A escolha nova vale na próxima sessão.
 *
 * É a mesma disciplina do loadout, das passivas, do pet e das cinco peças de
 * equipamento, e existe pela mesma razão: uma corrida tem de poder ser
 * re-simulada pelo servidor com exactamente o que ela tinha.
 *
 * ## Falha ABERTA, como a skin e ao contrário da nave
 *
 * Uma carta partida - uma cópia que deixou de ser da conta, uma definição
 * desligada, um nível impossível - é simplesmente DEIXADA DE FORA. Uma nave
 * partida tem de parar uma caçada, porque uma corrida configurada a partir dela
 * pagaria errado; uma carta partida não paga nada, e recusar uma caçada por
 * causa de uma habilidade seria um negócio muito pior do que jogar sem ela.
 *
 * ## Ordem canónica, sempre
 *
 * Pela ordem de `CARD_SLOTS`, com os vazios ausentes. A ordem decide qual carta
 * a activação automática considera primeiro, portanto ela é parte do resultado
 * e não uma conveniência de apresentação.
 */

/** Uma carta congelada num espaço. */
export interface FrozenCard {
  readonly slot: CardSlot;
  readonly instanceId: string;
  readonly definitionId: string;
  readonly level: number;
}

/** O que a sessão guarda: as cartas, e de que catálogo elas vieram. */
export interface CardLoadout {
  readonly cards: readonly FrozenCard[];
  /**
   * A versão do CONTEÚDO em que estas cartas foram congeladas.
   *
   * Guardada com elas, e não lida do build que restaura: é ela que permite a
   * uma corrida antiga dizer de que catálogo veio, e é ela que entra na
   * impressão digital.
   */
  readonly contentVersion: number;
}

export const EMPTY_CARD_LOADOUT: CardLoadout = Object.freeze({
  cards: Object.freeze([]),
  contentVersion: CARD_CONTENT_VERSION,
});

export const frozenCardSchema = z
  .object({
    slot: cardSlotSchema,
    instanceId: z.uuid(),
    definitionId: z.string().min(1),
    level: z.number().int().min(1).max(CARD_MAX_LEVEL),
  })
  .strict();

export const cardLoadoutSchema = z
  .object({
    cards: z.array(frozenCardSchema).max(CARD_SLOTS.length),
    contentVersion: z.number().int().min(1),
  })
  .strict();

/**
 * Lê o que está nos três espaços e devolve o que uma corrida pode congelar.
 *
 * Valida, por cópia: pertence a esta conta, está `owned`, não está reservada, a
 * definição existe, é da categoria `card`, está activada, e o nível cabe em
 * 1..6. O que falhar sai da lista, em silêncio e por desenho - ver a nota de
 * "falha aberta" acima.
 *
 * Não faz I/O e não conhece uma tabela: recebe linhas já lidas, exactamente
 * como `resolveLoadout` recebe as da nave e do canhão.
 */
export function resolveCardLoadout(input: {
  readonly accountId: string;
  readonly slots: readonly {
    readonly slot: CardSlot;
    readonly instance: EquippedInstanceInput | null;
  }[];
}): CardLoadout {
  const bySlot = new Map(input.slots.map((entry) => [entry.slot, entry.instance]));
  const cards: FrozenCard[] = [];
  for (const slot of CARD_SLOTS) {
    const instance = bySlot.get(slot) ?? null;
    if (!instance) {
      continue;
    }
    if (
      instance.ownerAccountId !== input.accountId ||
      instance.state !== 'owned' ||
      instance.reserved
    ) {
      continue;
    }
    const definition = cardDefinition(instance.definitionId);
    if (!definition) {
      continue;
    }
    if (instance.level < 1 || instance.level > CARD_MAX_LEVEL) {
      continue;
    }
    cards.push({
      slot,
      instanceId: instance.instanceId,
      definitionId: definition.definitionId,
      level: instance.level,
    });
  }
  return { cards, contentVersion: CARD_CONTENT_VERSION };
}

/**
 * A impressão digital destas cartas, para a época de uma amostra offline.
 *
 * Vazia quando não há carta nenhuma, e é isso que mantém a impressão de uma
 * conta sem cartas exactamente onde estava - a string não ganha um segmento
 * vazio.
 *
 * Leva o nível porque o nível muda o cooldown, o cooldown muda quantas vezes a
 * habilidade dispara, e isso muda a taxa de ganho. Leva a instância porque
 * trocar por uma cópia idêntica é uma escolha do jogador e deve começar uma
 * época nova. E leva a versão de conteúdo, pela mesma razão que a joia leva a
 * dela: o mesmo id pode valer outra coisa noutro catálogo.
 */
export function cardLoadoutFingerprint(loadout: CardLoadout | undefined): string {
  if (loadout === undefined || loadout.cards.length === 0) {
    return '';
  }
  const entries = loadout.cards
    .map((card) => `${card.slot}=${card.definitionId}@${card.level}:${card.instanceId}`)
    .join(',');
  return `k${loadout.contentVersion}:${entries}`;
}

export function parseCardLoadout(value: unknown): CardLoadout {
  return cardLoadoutSchema.parse(value);
}
